import { navigate } from "gatsby";

import Languages from "@data/languages.json";
import type Language from "@interfaces/Language";
import { useAuthenticationStore } from "@stores/authentication";
import { clearUserData } from "@utilities/helper";
import { default as defaultAxios } from "axios";

const autoLogoutPathname: string[] = ["/"];
const autoLogoutPathnameWithLanguages: string[] = [...autoLogoutPathname];
Languages.forEach((lang: Language) => {
  autoLogoutPathname.forEach((pathname: string) => {
    let formattedPathname = `${lang.id}${pathname}`;
    if (!formattedPathname.startsWith("/")) {
      formattedPathname = "/" + formattedPathname;
    }
    if (!formattedPathname.endsWith("/")) {
      formattedPathname = formattedPathname + "/";
    }
    autoLogoutPathnameWithLanguages.push(formattedPathname);
  });
});

const getHeaders = (): Record<string, string> => {
  const defaultHeaders: Record<string, string> = {
    Accept: "application/json",
    "Content-Type": "multipart/form-data",
  };

  const authenticationState = useAuthenticationStore.getState();
  if (authenticationState.isAuthenticated()) {
    defaultHeaders.Authorization = `Bearer ${authenticationState.getToken()}`;
  }

  return defaultHeaders;
};

const axios = defaultAxios.create({
  baseURL: `${
    process.env.GATSBY_API_BASE_URL?.endsWith("/")
      ? process.env.GATSBY_API_BASE_URL
      : process.env.GATSBY_API_BASE_URL + "/"
  }${process.env.GATSBY_API_VERSION ?? ""}`,
  timeout: 30000, // 30 seconds timeout
  headers: getHeaders(),
});

axios.interceptors.request.use(config => {
  const authenticationState = useAuthenticationStore.getState();
  if (authenticationState.isAuthenticated()) {
    config.headers.Authorization = `Bearer ${authenticationState.getToken()}`;
  }
  return config;
});

axios.interceptors.response.use(
  response => {
    return response;
  },
  async error => {
    if (error.response && error.response.status === 903) {
      const errorMessage = error.response.data.message.toLowerCase();
      if (errorMessage?.includes("token") || errorMessage.includes("expired")) {
        if (
          typeof window !== "undefined" &&
          window.location?.pathname &&
          window.location?.reload &&
          autoLogoutPathnameWithLanguages.includes(window.location.pathname)
        ) {
          clearUserData();
          window.location.reload();
        } else {
          navigate("/login?expired=1");
        }
      }
    }
    return await Promise.reject(error);
  },
);

export default axios;
